import { Checkbox, Grid, Paper } from '@mui/material';
import { useState } from 'react';
import { postToMultipleJobBoards, amplitude, IJob } from 'wisestep-api';
import { WsButton, WsColors, WsTypoColors, WsTypography, WsTypoVariants } from 'wisestep-base';

import { useWorkspaceContext, useWorkspaceSKUContext } from '../../../../../../../src/context';
import { ActivateFeature } from '../../../../../../components/ActivateFeature';
import TalentIcon from '../../../../../../images/icons/TalentDotComIcon.png';
import CareerJet from '../../../../../../images/jobBoardsIcons/CareerJet.png';
import Google from '../../../../../../images/jobBoardsIcons/Google.png';
import JobInventory from '../../../../../../images/jobBoardsIcons/JobInventory.png';
import JobisiteNew from '../../../../../../images/jobBoardsIcons/JobisiteNew.png';
import Jobomas from '../../../../../../images/jobBoardsIcons/Jobomas.png';
import JobVertise from '../../../../../../images/jobBoardsIcons/JobVertise.png';
import Jooble from '../../../../../../images/jobBoardsIcons/Jooble.png';
import Jora from '../../../../../../images/jobBoardsIcons/Jora.png';
import Learn4Good from '../../../../../../images/jobBoardsIcons/Learn4Good.png';
import Lensa from '../../../../../../images/jobBoardsIcons/Lensa.png';
import Monster from '../../../../../../images/jobBoardsIcons/Monster.png';
import Neuvoo from '../../../../../../images/jobBoardsIcons/Neuvoo.png';
import Oodle from '../../../../../../images/jobBoardsIcons/Oodle.png';
import PostJobFree from '../../../../../../images/jobBoardsIcons/PostJobFree.png';
import WhatJobs from '../../../../../../images/jobBoardsIcons/WhatJobs.png';
import YourCareerPage from '../../../../../../images/jobBoardsIcons/YourCareerPage.png';
import ZipRecruiterIcon from '../../../../../../images/jobBoardsIcons/ZipRecruiters_logo.jpg';
import { failed, warning } from '../../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../../utils/NotificationStore';
import { publishFailed, publishSuccess } from '../../helper/jobBoardHelper';
import JobPreviewScreen from '../../jobPreviewScreen/JobPreviewScreen';
import FreeJobBoardCard, { IJobBoard } from '../freeJobBoardCard/FreeJobBoardCard';

export interface IFreeJobBoardListProps {
  jobId: string;
  pageRefresh: () => void;
  job: IJob;
}

const FreeJobBoardsList = ({ jobId, pageRefresh, job }: IFreeJobBoardListProps) => {
  const workspaceSKU = useWorkspaceSKUContext();
  const freeJobPostingSku = workspaceSKU?.workspacePlan?.workspacePlanFeatures
    ? workspaceSKU?.workspacePlan?.workspacePlanFeatures.filter(
        (data) => data.featureCode === 'JS_EXTERNAL_JOB_POSTING_ORGANIC'
      )
    : [];
  const showFreeJobPosting = freeJobPostingSku?.[0]?.displayWhenInactive;
  const freeJobPostingAllowed = freeJobPostingSku?.[0]?.active;
  const workspace = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [jobBoards, setJobBoards] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [visibility, setVisibility] = useState<boolean>(false);

  const freeJobBoardPublish = async (jobBoardAccountEmail = '') => {
    if (freeJobPostingAllowed) {
      try {
        amplitude('Job_publish_success', {
          clickedOn: 'Publish',
          pageName: 'Publish jobs',
          pageUrl: window.location.href,
          workspaceName: workspace.name,
          planType: workspace.planType,
          interfaceType: 'Web',
        });
        if (jobBoards) await postToMultipleJobBoards(jobBoards, jobId, jobBoardAccountEmail);
        setVisibility(false);
        setNotification(publishSuccess());
        pageRefresh();
      } catch {
        setNotification(publishFailed());
      }
    } else {
      setNotification(
        failed(
          'Please contact us on support@wisestepmail.com if you would like to enable free job posting in your plan'
        )
      );
    }
  };

  const jobBoardsList: IJobBoard[] = [
    {
      name: 'Your Career Page',
      label: 'CareersPage',
      icon: <img src={YourCareerPage} alt={YourCareerPage} />,
    },
    {
      name: 'CareerJet',
      label: 'CareerJet',
      icon: <img src={CareerJet} alt={CareerJet} />,
    },
    {
      name: 'Google',
      label: 'GoogleJobs',
      icon: <img src={Google} alt={Google} />,
    },
    {
      name: 'Jooble',
      label: 'Jooble',
      icon: <img src={Jooble} alt={Jooble} />,
    },
    {
      name: 'Jora',
      label: 'jora' || 'joraUS' || 'joraUK',
      icon: <img src={Jora} alt={Jora} />,
    },
    {
      name: 'JobInventory',
      label: 'JobInventory',
      icon: <img src={JobInventory} alt={JobInventory} />,
    },
    {
      name: 'Jobvertise',
      label: 'Jobvertise',
      icon: <img src={JobVertise} alt={JobVertise} />,
    },
    {
      name: 'Jobisite',
      label: 'Jobisite',
      icon: <img src={JobisiteNew} alt={JobisiteNew} height={28} width={28} />,
    },
    {
      name: 'Lensa',
      label: 'Lensa' || 'LensaUS' || 'lensaUS',
      icon: <img src={Lensa} alt={Lensa} />,
    },
    {
      name: 'Learn4Good',
      label: 'Learn4good',
      icon: <img src={Learn4Good} alt={Learn4Good} />,
    },
    {
      name: 'Neuvoo',
      label: 'Neuvoo',
      icon: <img src={Neuvoo} alt={Neuvoo} />,
    },
    {
      name: 'Oodle',
      label: 'Oodle',
      icon: <img src={Oodle} alt={Oodle} />,
    },
    {
      name: 'Post Job Free',
      label: 'Postjobfree',
      icon: <img src={PostJobFree} alt={PostJobFree} />,
    },
    {
      name: 'Monster Organic',
      label: 'MonsterOrganic',
      icon: <img src={Monster} alt={Monster} height={43} />,
    },
    {
      name: 'Jobomas',
      label: 'Jobomas',
      icon: <img src={Jobomas} alt={Jobomas} />,
    },
    {
      name: 'What Jobs',
      label: 'whatjobs' || 'WhatJobs',
      icon: <img src={WhatJobs} alt={WhatJobs} />,
    },
    {
      name: 'ZipRecruiter',
      label: 'ZipRecruiter',
      icon: <img src={ZipRecruiterIcon} alt={ZipRecruiterIcon} height={29} />,
    },
    {
      name: 'Talent',
      label: 'Talent',
      icon: <img src={TalentIcon} alt={TalentIcon} height={29} />,
    },
  ];

  const onSelectAll = () => {
    amplitude('Job_publish_selectall', {
      clickedOn: 'Select all',
      pageName: 'Publish jobs',
      pageUrl: window.location.href,
      workspaceName: workspace.name,
      planType: workspace.planType,
      interfaceType: 'Web',
    });
    if (jobBoards?.length === jobBoardsList?.length) {
      setSelectAll(false);
      setJobBoards([]);
    } else {
      const temp: string[] = [];
      jobBoardsList.map((jobBoard) => temp.push(jobBoard.label ?? ''));
      setJobBoards(temp);
    }
    setSelectAll(!selectAll);
  };

  return (
    <>
      {showFreeJobPosting && (
        <>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Grid container>
              <Grid item xs={9} alignContent="flex-start">
                <WsTypography variant={WsTypoVariants.SUB_TITLE}>Free Job Boards</WsTypography>
              </Grid>
              <Grid item xs={3} alignContent="flex-end">
                <ActivateFeature feature="JS_EXTERNAL_JOB_POSTING_ORGANIC">
                  <WsButton
                    onClick={() => {
                      if (jobBoards?.length === 0) {
                        setNotification(
                          warning('Please Select at least one Job Board to continue')
                        );
                      } else {
                        setVisibility(true);
                      }
                    }}
                  >
                    Publish
                  </WsButton>
                </ActivateFeature>
              </Grid>
              <Grid item xs={6} alignItems="flex-start" mt={3}>
                <WsTypography color={WsTypoColors.SECONDARY}>
                  {'SELECTED(' + jobBoards?.length + ')'}
                </WsTypography>
              </Grid>
              <Grid item xs={6} textAlign="end" mt={2} pr={2} onChange={onSelectAll}>
                <WsButton variant="text" size="medium" onClick={onSelectAll}>
                  Select All
                </WsButton>
                <Checkbox
                  onChange={onSelectAll}
                  sx={{ color: WsColors.TertiaryShades500 }}
                  checked={selectAll}
                />
              </Grid>
            </Grid>
            <FreeJobBoardCard
              jobBoards={jobBoardsList}
              checkedEl={jobBoards}
              setSelectAll={setSelectAll}
              selectedJobBoards={(jobBoardNames: string[]) => setJobBoards(jobBoardNames)}
            />
          </Paper>
        </>
      )}
      <JobPreviewScreen
        onPublish={() => freeJobBoardPublish()}
        visibility={visibility}
        onClose={() => setVisibility(false)}
        job={job}
      />
    </>
  );
};
export default FreeJobBoardsList;
